// --------------------------------------------------------------
// Created On: 2024-03-26
// Author: Dimitra Weinstein
//
// Last Modified: 2026-01-09
// Modified By: Dimitra Weinstein
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import PartsListItem from "./PartsListItem/PartsListItem";
import styles from "./PartsList.module.scss";

// Renders list of parts within given bom.
export default function PartsList(props: Props): Component {
  return (
    <div className={`${styles.body} card`}>
      <div data-test="parts-list-card" className="card-body">
        <h5 data-test="parts-list-title" className="card-title border-bottom">
          {props.bomPath.length > 0 && (
            <div className={`row text-center ${styles.partsListTitle}`}>
              <p>{props.bomPath.slice(-1)}</p>
            </div>
          )}
        </h5>
        <div className={`row ${styles.partsList}`}>
          <table className={`col ${styles.table} table table-borderless mb-2 pb-0`}>
            <tbody>
              <tr className="border-bottom pb-1">
                <th>Description</th>
                <th>Part Number</th>
                <th>Build Qty.</th>
                <th>Order Qty.</th>
                <th>Price</th>
                <th>Discount Price</th>
                <th>Weight</th>
                <th>Ship Time</th>
                <th></th>
              </tr>
              {props.currentParts.map((part, i) => (
                <PartsListItem
                  key={i}
                  bomIsFlat={props.bomIsFlat}
                  partId={part.partId}
                  bomPath={props.bomPath}
                  isHovered={props.hoveredPartNumber !== null && part.partNumber.includes(props.hoveredPartNumber)}
                  partNumber={part.partNumber}
                  description={part.description}
                  unitId={part.unitId}
                  units={part.units}
                  price={part.price}
                  buildQuantity={part.buildQuantity.toString()}
                  orderQuantity={part.orderQuantity}
                  weight={part.weight}
                  isSellable={part.isSellable}
                  hasOtherMaterial={part.hasOtherMaterial}
                  isPaintable={part.isPaintable}
                  isImpeller={part.isImpeller}
                  defaultPaintColorId={props.defaultPaintColorId}
                  parts={part.parts}
                  companyDiscount={props.companyDiscount}
                  onQuantityChange={(orderQuantity) => props.onQuantityChange(part.partId, orderQuantity)}
                  onHover={(partNumber) => props.onHover(partNumber)}
                  onNavigate={(bomPath) => props.onNavigate(bomPath)}
                  onCustomizePart={(selectedPartId) => props.onCustomizePart(selectedPartId)}
                  onAddToCartError={(message) => props.onAddToCartError(message)}
                  onAddToCart={(message) => props.onAddToCart(message)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="col-12 py-3 text-center">
        <button
          data-test="add-orderable-parts-btn"
          type="button"
          className="btn btn-primary btn-md"
          onClick={() => {
            props.onAddAllCurrentPartsToCart();
          }}
        >
          Add Orderable Parts To Cart
        </button>
      </div>
    </div>
  );
}

interface Props {
  loading: boolean;
  failedToLoad: boolean;
  bomIsFlat: boolean;
  errorMessage: string;
  bomPath: string[];
  defaultPaintColorId: number | null;
  currentParts: Part[];
  companyDiscount: number | null;
  hoveredPartNumber: string | null;
  onHover: (partNumber: string | null) => void;
  onNavigate: (bomPath: string[]) => void;
  onQuantityChange: (partId: number, orderQuantity: string) => void;
  onCustomizePart: (partId: number) => void;
  onAddToCartError: (message: string) => void;
  onAddToCart: (message: string) => void;
  onAddAllCurrentPartsToCart: () => void;
}

interface BomMapGetPart {
  partId: number;
  partNumber: string;
  description: string;
  unitId: number;
  units: string;
  price: number;
  buildQuantity: string;
  weight: number;
  isPaintable: boolean;
  isSellable: boolean;
  isImpeller: boolean;
  hasOtherMaterial: boolean;
  parts: BomMapGetPart[];
}

interface Part {
  partId: number;
  partNumber: string;
  description: string;
  unitId: number;
  units: string;
  price: number;
  buildQuantity: string;
  paintColorId: number | null;
  orderQuantity: string;
  weight: number;
  isPaintable: boolean;
  isSellable: boolean;
  isImpeller: boolean;
  impellerTrim: string | null;
  impellerAngle: string | null;
  hasOtherMaterial: boolean;
  parts: BomMapGetPart[];
}
